import {
    ActivityTicketPax,
    ActivityTicketSpecificField,
    BookingQuestionAnswerType,
    MandatoryFieldId,
} from '@/api/Cart/cart.interfaces';
import { isMandatory } from '@shared/helpers/product';
import {
    isDate,
    isDateAfter,
    isDecimal,
    isDefinedBoolean,
    isEmail,
    isInteger,
    isName,
    isPassportNumber,
    isPersonalIdNumber,
    isPhoneNumber,
    isRequired,
    isValidCharacters,
} from '@ui/components/FormV2/Validations/functions';
import { today } from '@shared/helpers/datetime/datetime';
import { isBoolean, isString } from 'lodash';
import { Validation } from '@ui/components/FormV2/Validations/Validations.interfaces';
import { t } from '@shared/helpers/translation';
import { AgeRange } from '@/api/Checkout/checkout.interfaces';
import {
    getTranslatedDateHint,
    getTranslatedDatetimeHint,
} from '@shared/helpers/datetime/datetime.formatters';
import { Market } from '@shared/config/market/market.models';
import { isValidDateOfBirth } from '@/helpers/checkout';
import { isSpecialRequirementsField } from '@shared/helpers/Cart/Activity';
import store from '@/store';

function getTicketObj(ticketIndex: number): string {
    return `ticket${ticketIndex}`;
}

function getCustomProp(id: string): string {
    return `${id}_custom`;
}

export function isShortTextField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.ShortText;
}
export function isLongTextField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.LongText;
}
export function isIntegerField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.Integer;
}
export function isDecimalField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.Decimal;
}
export function isDateField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.Date;
}
export function isDateAndTimeField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.DateAndTime;
}
export function isBooleanField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.Boolean;
}
export function isListOfValuesField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.ListOfValues;
}
export function isListOfValuesOrTextField(field: ActivityTicketSpecificField) {
    return field.ExpectedAnswerType === BookingQuestionAnswerType.ListOfValuesOrText;
}

function getTicketSpecificFields(
    specificFields: ActivityTicketSpecificField[],
    ticketIndex: number
) {
    const fields: any[] = [];

    specificFields.forEach((f) => {
        switch (f.ExpectedAnswerType) {
            case BookingQuestionAnswerType.ShortText:
            case BookingQuestionAnswerType.LongText:
                if (isSpecialRequirementsField(f.Id)) {
                    fields.push(getSimpleAnswerField(f, ticketIndex, []));
                    return;
                } else {
                    fields.push(getSimpleAnswerField(f, ticketIndex, [isRequired]));
                    return;
                }

            case BookingQuestionAnswerType.Integer:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isInteger]));
                return;

            case BookingQuestionAnswerType.Decimal:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isDecimal]));
                return;

            case BookingQuestionAnswerType.Date:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isDate]));
                return;

            case BookingQuestionAnswerType.DateAndTime:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isDate]));
                return;

            case BookingQuestionAnswerType.Boolean:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isDefinedBoolean]));
                return;

            case BookingQuestionAnswerType.ListOfValuesOrText:
                fields.push(getSimpleAnswerField(f, ticketIndex, [isRequired]));
                return;

            case BookingQuestionAnswerType.ListOfValues:
                switch (f.CanSelectMultipleOptions) {
                    case false:
                        fields.push(getSimpleAnswerField(f, ticketIndex, [isRequired]));
                        return;
                    case true:
                        fields.push(getMultipleAnswerField(f, ticketIndex, []));
                        return;
                }
        }
    });

    return fields;
}

function getSimpleAnswerField(
    ticketField: ActivityTicketSpecificField,
    ticketIndex: number,
    validations: Function[]
) {
    return {
        obj: getTicketObj(ticketIndex),
        prop: ticketField.Id,
        value: ticketField.Answer[0],
        validation: validations,
    };
}

function getMultipleAnswerField(
    ticketField: ActivityTicketSpecificField,
    ticketIndex: number,
    validations: Function[]
) {
    return {
        obj: getTicketObj(ticketIndex),
        prop: ticketField.Id,
        value: ticketField.Answer,
        validation: validations,
    };
}

interface ProductField {
    obj: string;
    prop: string;
    value: any | null | undefined;
    validation: any[];
}

export function getCustomAnswerField(
    ticketField: ActivityTicketSpecificField,
    ticketIndex: number,
    validations: Validation[]
): ProductField {
    return {
        obj: getTicketObj(ticketIndex),
        prop: getCustomProp(ticketField.Id),
        value: ticketField.Answer[0],
        validation: validations,
    };
}

export function getMandatoryFields(
    pax: ActivityTicketPax,
    ticketIndex: number,
    ageRange: AgeRange,
    productDate: Date
) {
    const obj = getTicketObj(ticketIndex);
    const paxMandatoryFields = pax.MandatoryFields;
    const fields: any[] = [];

    fields.push({
        obj,
        prop: 'gender',
        value: pax.Gender,
        validation: [isRequired],
    });

    fields.push({
        obj,
        prop: 'firstName',
        value: pax.FirstName,
        validation: [isRequired, isName],
    });

    fields.push({
        obj,
        prop: 'lastName',
        value: pax.LastName,
        validation: [isRequired, isName],
    });

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.PERSONAL_ID_NUMBER)) {
        fields.push({
            obj,
            prop: 'personalIdNumber',
            value: pax.PersonalIdNumber,
            validation: [isRequired, isPersonalIdNumber],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.EMAIL)) {
        fields.push({
            obj,
            prop: 'email',
            value: pax.Email,
            validation: [isRequired, isEmail],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.PHONE_NUMBER)) {
        fields.push({
            obj,
            prop: 'phone',
            value: pax.Phone,
            validation: [isRequired, isPhoneNumber],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.NATIONALITY)) {
        fields.push({
            obj,
            prop: 'nationality',
            value: pax.Nationality,
            validation: [isRequired],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.ORGANIZATION)) {
        fields.push({
            obj,
            prop: 'organization',
            value: pax.Organization,
            validation: [isRequired],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.PASSPORT_ID)) {
        fields.push({
            obj,
            prop: 'passportNumber',
            value: pax.PassportNumber,
            validation: [isRequired, isPassportNumber],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.PASSPORT_EXPIRY)) {
        fields.push({
            obj,
            prop: 'passportExpirationDate',
            value: pax.PassportExpirationDate,
            validation: [
                isRequired,
                isDateAfter(today(), t({ key: 'shared.expired_passport_date' })),
            ],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.ADDRESS)) {
        fields.push({
            obj,
            prop: 'address',
            value: pax.Address,
            validation: [isRequired, isValidCharacters],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.DATE_OF_BIRTH)) {
        fields.push({
            obj,
            prop: 'dateOfBirth',
            value: pax.DateOfBirth,
            validation: [(date: string) => isValidDateOfBirth(date, ageRange, productDate)],
        });
    }

    if (isMandatory(paxMandatoryFields, MandatoryFieldId.LANGUAGE)) {
        fields.push({
            obj,
            prop: 'language',
            value: pax.Language,
            validation: [isRequired],
        });
    }

    const specificFields = getTicketSpecificFields(pax.TicketFields, ticketIndex);
    fields.push(...specificFields);

    return fields;
}

export function formatTicketFieldName(ticketIndex: number | string, name: string) {
    return `ticket${ticketIndex}_${name}`;
}

export function parseBoolean(value: any) {
    if (isBoolean(value)) {
        return value;
    }
    if (isString(value)) {
        value = value.toLowerCase();
        if (value === 'true') return true;
        if (value === 'false') return false;
    }
}

export function getDateHint() {
    const date = new Date(1953, 7, 20);
    const market: Market = store.getters['general/getMarket'];
    const formattedDate = getTranslatedDateHint(date, market.fullLocale);

    return `${t({ key: 'shared.example', suffix: ': ' })}${formattedDate}`;
}

export function getDatetimeHint() {
    const date = new Date(2022, 2, 25, 10, 43);
    const market: Market = store.getters['general/getMarket'];
    const formattedDate = getTranslatedDatetimeHint(date, market.fullLocale);

    return `${t({ key: 'shared.example', suffix: ': ' })}${formattedDate}`;
}

export type CustomField = {
    index: number;
    field: ProductField;
};
